/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DiagramModules
 */
// eslint-disable-next-line max-classes-per-file
import {
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';

import {
    Subject,
    takeUntil,
} from 'rxjs';

import { attachComponentBindings } from 'nsx-alb-ui-components';
import { ConvertService } from 'ng/modules/core/services/convert-service/convert.service';
import { getTickFormatFunction } from 'ng/shared/utils/date.utils';
import { L10nService } from '@vmw/ngx-vip';
import { num2abbrStr, toFixed } from 'ng/shared/index';
import { selectUtcTime } from 'ng/root-store/user-preferences/user-preferences.selectors';
import { Store } from '@ngrx/store';
import { throttle } from 'underscore';
import { Timeframe } from 'ajs/js/services/Timeframe';
import * as d3 from 'd3';
import * as globalL10n from 'global-l10n';

import {
    IAreaDataPoint,
    IEventsDataPoint,
    IGraphIconData,
    IGroupData,
    ILabelDataExtended,
} from './avi-area-graph.types';

import './avi-area-graph.component.less';
import { getUnixTimeInMilliseconds } from '../avi-bar-graph/avi-bar-graph.utils';
import { LabelCountLegendComponent } from '../label-count-legend';
import { TTimestampFormatFromApi } from '../avi-bar-graph/avi-bar-graph.types';
import * as l10n from './avi-area-graph.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type AreaDataPointKeys = keyof IAreaDataPoint;

/** Constants representing elements d3 will work with. */
const COMPONENT_NAME = 'avi-area-graph';
const DATE_KEY = 'timestamp';
const G_ELEMENT = 'g';
const PATH_ELEMENT = 'path';
const SVG_ELEMENT = 'svg';
const TOOLTIP_STROKE = 'var(--cds-global-color-blue-700)';

/**
 * Margin (in pixels) for the SVG elements.
 */
const margin = {
    bottom: 20,
    left: 40,
    right: 20,
    top: 15,
};

const tooltipMarginFromLine = 50;
const tooltipMinWidth = 215;
const yScaleMultiplier = 1.15;
const zoomScaleFactor = 1.2;
const animationDuration = 400;

/** Class name constants representing classes d3 will work with.  */
const AVI_AREA_GRAPH_ACTIVE_TIMESTAMP_CLASS = `${COMPONENT_NAME}__active-timestamp`;
const AVI_AREA_GRAPH_AREA_CLASS = `${COMPONENT_NAME}__area`;
const AVI_AREA_GRAPH_GROUP_CONTAINER_CLASS = `${COMPONENT_NAME}__group-container`;
const AVI_AREA_GRAPH_LAYER_CLASS = `${COMPONENT_NAME}__layer`;
const AVI_AREA_GRAPH_LAYER_Y1_CLASS = `${COMPONENT_NAME}__layer-y1`;
const AVI_AREA_GRAPH_SVG_CONTAINER_CLASS = `${COMPONENT_NAME}__svg-container`;
const AVI_AREA_GRAPH_TOOLTIP_CLASS = `${COMPONENT_NAME}__tooltip-container`;
const AVI_AREA_GRAPH_TOOLTIP_LINE_ID = `${COMPONENT_NAME}__tooltip-line`;
const AVI_AREA_GRAPH_X_AXIS_CLASS = `${COMPONENT_NAME}__x-axis`;
const AVI_AREA_GRAPH_Y_AXIS_CLASS = `${COMPONENT_NAME}__y-axis`;
const AVI_AREA_GRAPH_Y1_AXIS_CLASS = `${COMPONENT_NAME}__y1-axis`;

// Grid Names
const enum gridNames {
    anomalies = 'anomalies',
    alerts = 'alerts',
    configEvents = 'configEvents',
    systemEvents = 'systemEvents',
}

const transparentColor = 'transparent';

/**
 * ResizeObserver exists in browsers, but nsx-alb doesn't inherently know about it.
 * This is an 'ambient declaration', so we can declare this type.
 * Done to avoid installing an external lib (i.e. @types/resize-observer-browser) just
 * for this one use case.
 */
declare class ResizeObserver {
    constructor(callback: ResizeObserverCallback);

    public observe(target: Element): void;
    public unobserve(target: Element): void;
    public disconnect(): void;
}

type ResizeObserverCallback = (entries: any[], observer: ResizeObserver) => void;

/**
 * @description Reusable component: Create area graph for values spanning time.
 * @author Deepak Chaudhary
 */
@Component({
    selector: COMPONENT_NAME,
    templateUrl: './avi-area-graph.component.html',
})
export class AviAreaGraphComponent implements OnChanges, OnDestroy {
    @ViewChild('chart')
    public readonly chart: ElementRef;

    /** Ref to manipulate tooltip container. */
    @ViewChild('aviAreaGraphTooltipContainerElementRef', {
        read: ElementRef,
    })
    public aviAreaGraphTooltipContainerElementRef: ElementRef;

    /** Ref to pass in tooltip component. */
    @ViewChild('aviAreaGraphTooltipRef', {
        read: ViewContainerRef,
        static: true,
    })
    public aviAreaGraphTooltipRef: ViewContainerRef;

    @Input()
    public graphId: string;

    @Input()
    public isLoading: boolean;

    @Input()
    public areaGraphHeight = 300;

    @Input()
    public labelList: ILabelDataExtended[];

    @Input()
    public dataset: IAreaDataPoint[];

    @Input()
    public showButtons: boolean;

    @Input()
    public showLegends: boolean;

    @Input()
    public showTooltip: boolean;

    @Input()
    public showAxis: boolean;

    @Input()
    public minYScale: number;

    @Input()
    public minY1Scale: number;

    @Input()
    public anomaliesCount: number;

    @Input()
    public alertsCount: number;

    @Input()
    public configEventsCount: number;

    @Input()
    public systemEventsCount: number;

    @Input()
    public unit: string;

    @Input()
    public hasTotalValueInTooltip: boolean;

    @Input()
    public configEvents: IEventsDataPoint[];

    @Input()
    public alerts: IEventsDataPoint[];

    @Input()
    public systemEvents: IEventsDataPoint[];

    @Input()
    public anomalies: IEventsDataPoint[];

    @Input()
    public activeTimestamp: number;

    @Input()
    public currentActiveGridInfo: IGraphIconData;

    @Input()
    public has2YAxis: boolean;

    /**
     * Fire on Event on graph icon click.
     */
    @Output()
    public onGraphIconClick = new EventEmitter<IGraphIconData>();

    /**
     * Fire on Event on graph hover to set active timestamp.
     */
    @Output()
    public setActiveTimeStamp = new EventEmitter<number>();

    public graphWidth: number;

    public tooltipWidth: number;

    public showAnomaly = true;

    public showAlerts = true;

    public showConfigEvents = true;

    public showSystemEvents = true;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    /** Position of tooltip (if exists) from left of graph container. */
    public tooltipLeftPositionPx: number;

    /** The main SVG element for the graph. */
    private svg: d3.Selection<SVGSVGElement, undefined, null, undefined>;

    /** The main G element for the graph. */
    private g: d3.Selection<SVGGElement, undefined, null, undefined>;

    private xScale: d3.ScaleTime<number, number, never>;

    private yScale: d3.ScaleLinear<number, number, never>;

    private y1Scale: d3.ScaleLinear<number, number, never>;

    private groupedData: IGroupData[];

    /** The y-axis zoom factor for the graph. */
    private yZoomFactor = 1;

    private graphHeight: number;

    private graphStackKeys: string[];

    /** The area function for the graph. */
    private graphArea: d3.Area<[number, number]>;

    private graphY1Area: d3.Area<[number, number]>;

    private stackedData: Array<d3.Series<IGroupData, string>>;

    private stackedY1Data: Array<d3.Series<IGroupData, string>>;

    private graphMargin = margin;

    private isGraphHoverPaused = false;

    /**
     * Flag to check if x-axis labels should follow UTC timezone.
     */
    private useUtcTime = false;

    private ngDestroyed$ = new Subject<void>();

    /**
     * Flag to track if the graph is already created.
     * x-axis should be redrawn only if the graph has already been created.
     */
    private isGraphCreated = false;

    private maxValueHash: Record<number, number> = {};

    /**
     * Set of keys for which data doesn't need to be plotted on the graph but needs to be displayed
     * in the tooltip. Eg. 'l7_client.pct_response_errors'
     */
    private hiddenKeySet = new Set<string>();

    /**
     * Set of keys when user click on legends to toggle. Eg. 'l7_client.pct_response_errors'
     */
    private hiddenLegendsKeySet = new Set<string>();

    private graphContainerWidth: number;

    /**
     * Added to component state to allow for unsubscribing.
     */
    private graphResizeObserver: ResizeObserver;

    constructor(
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly l10nService: L10nService,
        private readonly convertService: ConvertService,
        private readonly store: Store,
        private readonly ngZone: NgZone,
        @Inject('Timeframe')
        private readonly timeframeService: Timeframe,
    ) {
        l10nService.registerSourceBundles(dictionary);

        store.select(selectUtcTime).pipe(
            takeUntil(this.ngDestroyed$),
        ).subscribe(
            useUtcTime => {
                this.useUtcTime = useUtcTime;

                if (this.isGraphCreated) {
                    this.refreshGraph();
                }
            },
        );
    }

    public get isZoomOutDisabled(): boolean {
        return this.yZoomFactor === 1;
    }

    private static getDataValue(data: IGroupData, key: AreaDataPointKeys): number {
        return data[key] as number || 0;
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.dataset?.isFirstChange() || this.isLoading) {
            return;
        }

        if (changes.has2YAxis) {
            this.graphMargin.right = this.has2YAxis ? 40 : 20;
        }

        const isDatasetNonEmpty = this.dataset.length > 0;

        if (
            changes.dataset?.currentValue && isDatasetNonEmpty &&
            JSON.stringify(changes.dataset?.previousValue) !== JSON.stringify(changes.dataset?.currentValue)
        ) {
            this.hiddenKeySet = new Set(
                this.labelList.filter(label => label.hideFromGraph && label.name).map(label => label.name ?? ''),
            );

            this.hiddenLegendsKeySet.clear();
            this.refreshGraph();
        }

        if (changes.labelList?.currentValue && isDatasetNonEmpty) {
            this.yZoomFactor = 1;
        }

        if (
            !changes.activeTimestamp?.isFirstChange() && !this.isLoading &&
            this.activeTimestamp > 0 && isDatasetNonEmpty
        ) {
            this.refreshActiveTimestampCircle();
        }

        const areAlertsChangedToNonEmptyValues =
            Boolean(changes.alerts?.currentValue && changes.alerts.currentValue.length > 0);
        const areAnomaliesChangedToNonEmptyValues =
            Boolean(changes.anomalies?.currentValue && changes.anomalies.currentValue.length > 0);
        const areConfigEventsChangedToNonEmptyValues =
            Boolean(changes.configEvents?.currentValue && changes.configEvents.currentValue.length > 0);
        const areSystemEventsChangedToNonEmptyValues =
            Boolean(changes.systemEvents?.currentValue && changes.systemEvents.currentValue.length > 0);

        if (isDatasetNonEmpty && (areAlertsChangedToNonEmptyValues || areAnomaliesChangedToNonEmptyValues ||
            areConfigEventsChangedToNonEmptyValues || areSystemEventsChangedToNonEmptyValues) && !this.isLoading) {
            this.setEventsPoints();
        }

        if (!this.graphResizeObserver) {
            this.setGraphResizeObserver();
        }
    }

    /**
     * Handle the zoom in action for the graph.
     * Increase the y-axis zoom factor and updates the y-axis accordingly.
     */
    public handleZoomIn(): void {
        let minValue = 0;
        let currentYScaleMax = 0;

        if (this.has2YAxis) {
            const leftAxisKeys = this.graphStackKeys.slice(0, -1);

            minValue = d3
                .max(this.groupedData, (data: IGroupData) => d3
                    .min(leftAxisKeys, (key: AreaDataPointKeys) => AviAreaGraphComponent
                        .getDataValue(data, key))) as number;

            currentYScaleMax = this.yScale.domain()[1];
        } else {
            minValue = d3
                .max(this.groupedData, (data: IGroupData) => d3
                    .min(this.graphStackKeys, (key: AreaDataPointKeys) => AviAreaGraphComponent
                        .getDataValue(data, key))) as number;

            currentYScaleMax = this.yScale.domain()[1];
        }

        if (currentYScaleMax <= minValue || !minValue) {
            return;
        }

        this.yZoomFactor *= zoomScaleFactor;
        this.updateYAxis();
    }

    /**
     * Handle the zoom out action for the graph.
     * Decrease the y-axis zoom factor and updates the y-axis accordingly.
     */
    public handleZoomOut(): void {
        if (this.yZoomFactor > 1) {
            this.yZoomFactor /= zoomScaleFactor;

            if (this.yZoomFactor < 1) {
                this.yZoomFactor = 1;
            }

            this.updateYAxis();
        }
    }

    public onGraphIcon(event: IEventsDataPoint, activeGrid: string): void {
        // Reset Previous Active Graph Icons
        switch (this.currentActiveGridInfo?.activeGrid) {
            case gridNames.alerts:
                this.alerts.forEach(alert => alert.isActive = false);
                break;
            case gridNames.anomalies:
                this.anomalies.forEach(anomaly => anomaly.isActive = false);
                break;
            case gridNames.configEvents:
                this.configEvents.forEach(configEvent => configEvent.isActive = false);
                break;
            case gridNames.systemEvents:
                this.systemEvents.forEach(systemEvent => systemEvent.isActive = false);
                break;
        }

        event.isActive = true;

        this.onGraphIconClick.emit({
            ...event,
            activeGrid,
        });
    }

    public toggleLegends(legend: ILabelDataExtended): void {
        const { name } = legend;

        if (!name) {
            return;
        }

        if (this.hiddenLegendsKeySet.has(name)) {
            this.hiddenLegendsKeySet.delete(name);
        } else {
            this.hiddenLegendsKeySet.add(name);
        }

        this.refreshGraph();
    }

    /**
     * trackBy Index Method used in template.
     */
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * trackBy Label method used in the label-color-optional template.
     */
    public trackByLabel(index: number, label: ILabelDataExtended): string {
        return label.label;
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.complete();
        this.ngDestroyed$.unsubscribe();

        if (this.graphResizeObserver) {
            this.graphResizeObserver.disconnect();
        }
    }

    /**
     * Transform time into title to display in tooltip.
     */
    private getTooltipTitle(date: TTimestampFormatFromApi): string {
        const formatTime = this.useUtcTime ? d3.utcFormat('%m-%d %I:%M:%S %p') :
            d3.timeFormat('%m-%d %I:%M:%S %p');

        return formatTime(new Date(getUnixTimeInMilliseconds(date)));
    }

    /**
     * Remove the SVG elements for the x-axis and y-axis from the graph.
     * This function is useful when needing to clear or refresh the axes.
     */
    private removeSvg(): void {
        d3
            .select(`#${this.graphId}`)
            .select(`.${AVI_AREA_GRAPH_GROUP_CONTAINER_CLASS}`)
            .remove();
        d3
            .select(`#${this.graphId}`)
            .select(`.${AVI_AREA_GRAPH_X_AXIS_CLASS}`)
            .remove();
        d3
            .select(`#${this.graphId}`)
            .select(`.${AVI_AREA_GRAPH_Y_AXIS_CLASS}`)
            .remove();
        d3
            .select(`#${this.graphId}`)
            .select(`.${AVI_AREA_GRAPH_Y1_AXIS_CLASS}`)
            .remove();
    }

    /**
     * Initialize / Refresh the graph by clearing the SVG, creating the SVG element, appending the graph group,
     * setting the graph height and width, creating the grouped data, scales, graph, x-axis and y-axis.
     */
    private refreshGraph(): void {
        if (!this.showAxis) {
            this.graphMargin = {
                bottom: 0,
                left: 0,
                right: 0,
                top: 0,
            };
        }

        this.removeSvg();
        this.createSvg();
        this.appendGroup();

        this.graphHeight = +this.svg.attr('height') - this.graphMargin.top - this.graphMargin.bottom;
        this.graphWidth = +this.svg.attr('width') - this.graphMargin.left - this.graphMargin.right;

        this.graphStackKeys = Array.from(new Set(this.dataset.filter(
            dataSet => dataSet.name &&
                !this.hiddenKeySet.has(dataSet.name) &&
                !this.hiddenLegendsKeySet.has(dataSet.name),
        ).map(dataSet => dataSet.name)));

        this.createGroupData();
        this.createScales();
        this.createGraph();
        this.createXAxis();
        this.createYAxis();
        this.setEventsPoints();

        this.isGraphCreated = true;
    }

    /**
     * Create a SVG element for the graph if it doesn't exist.
     */
    private createSvg(): void {
        this.graphContainerWidth = this.getGraphContainerWidth();

        this.svg = this.svg ?? d3
            .select<SVGSVGElement, undefined>(`#${this.graphId}`)
            .append<SVGSVGElement>(SVG_ELEMENT)
            .attr('height', this.areaGraphHeight)
            .attr('width', this.graphContainerWidth)
            .classed(AVI_AREA_GRAPH_SVG_CONTAINER_CLASS, true) as typeof this.svg;
    }

    /**
     * Create a D3 area generator function for the graph. This function will be used to generate
     * the path data for the area graph, mapping data points to the corresponding x and y coordinates.
     */
    private createGraphArea(): void {
        if (this.has2YAxis) {
            this.graphArea = d3
                .area<[number, number]>()
                .curve(d3.curveMonotoneX)
                .y0(this.yScale(0))
                .y1(d => this.yScale(d[1]))
                .x((_, i) => this.xScale(this.groupedData[i].timestamp));

            this.graphY1Area = d3
                .area<[number, number]>()
                .curve(d3.curveMonotoneX)
                .y0(this.y1Scale(0))
                .y1(d => this.y1Scale(d[1]))
                .x((_, i) => this.xScale(this.groupedData[i].timestamp));
        } else {
            this.graphArea = d3
                .area<[number, number]>()
                .x((_, i) => this.xScale(this.groupedData[i].timestamp))
                .y0(d => this.yScale(d[0]))
                .y1(d => this.yScale(d[1]));
        }
    }

    private getColor(key: string, isStrokeColor: boolean, isGraphColor: boolean): string {
        const labelObj = this.labelList.find(label => label.name === key);

        if (!labelObj) {
            return '';
        }

        const {
            color,
            isTransparent,
            strokeColor,
        } = labelObj;

        if (isStrokeColor) {
            return strokeColor ?? '';
        }

        if (isTransparent && isGraphColor) {
            return transparentColor;
        }

        return color ?? '';
    }

    /**
     * The stack is a collection of layers, each layer represents a unique name. It is created by grouping
     * the dataset by timestamp and aggregating values. It's then sorted by the timestamp of the data points.
     *
     * The stacked data is the result of creating the stack. It's an array of layers,
     * each layer represents a unique name. It's sorted by the timestamp of the data points.
     */
    private createGraphStack(): void {
        if (this.has2YAxis) {
            const leftAxisKeys = this.graphStackKeys.slice(0, -1);
            const rightAxisKeys = this.graphStackKeys.slice(-1);

            const stack = d3
                .stack<IGroupData>()
                .keys(leftAxisKeys)
                .value((data: IGroupData, key: AreaDataPointKeys) => AviAreaGraphComponent.getDataValue(data, key));

            this.stackedData = stack(this.groupedData);

            const stackY1 = d3
                .stack<IGroupData>()
                .keys(rightAxisKeys)
                .value((data: IGroupData, key: AreaDataPointKeys) => AviAreaGraphComponent.getDataValue(data, key));

            this.stackedY1Data = stackY1(this.groupedData);
        } else {
            const stack = d3
                .stack<IGroupData>()
                .keys(this.graphStackKeys)
                .value((data: IGroupData, key: AreaDataPointKeys) => AviAreaGraphComponent.getDataValue(data, key));

            this.stackedData = stack(this.groupedData);
        }
    }

    /**
     * Group dataset by timestamp and aggregate values.
     * Convert the dataset into a format suitable for stacked area graph.
     * Each group represents a unique timestamp with aggregated values by name.
     */
    private createGroupData(): void {
        this.groupedData = d3
            .groups(this.dataset, (d: IAreaDataPoint) => d.timestamp)
            .map(([key, values]) => {
                this.maxValueHash[key] = values.reduce((acc, data) => {
                    acc += Number(data.value) || 0;

                    return acc;
                }, 0);

                return {
                    timestamp: key,
                    ...Object.fromEntries(
                        d3.rollup(
                            values,
                            v => d3.sum(v, (d: IAreaDataPoint) => d.value),
                            (d: IAreaDataPoint) => d.name,
                        ),
                    ),
                };
            });
    }

    /**
     * Append a group element to the SVG.
     * This group element contains all the graphical elements of the graph.
     */
    private appendGroup(): void {
        this.g = this.svg
            .append(G_ELEMENT)
            .attr('transform', `translate(${this.graphMargin.left},${this.graphMargin.top})`)
            .classed(AVI_AREA_GRAPH_GROUP_CONTAINER_CLASS, true);
    }

    /**
     * It create the area generator function and the stack generator function.
     * It then appends the layers of the graph to the SVG.
     * Each layer is a group element which contains the path element of the area graph.
     * The path element is assigned a class so that it can be styled.
     * The fill color of the path element is determined by the color of the corresponding label.
     */
    private createGraph(): void {
        this.aviAreaGraphTooltipRef.clear();
        this.createGraphArea();
        this.createGraphStack();

        this.svg
            .on('mouseover', event => this.refreshTooltip(event))
            .on('mousemove', event => this.refreshTooltip(event, true))
            .on('mouseout', () => this.removeToolTip())
            .on('click', () => this.isGraphHoverPaused = !this.isGraphHoverPaused);

        if (this.has2YAxis) {
            this.g
                .selectAll(`.${AVI_AREA_GRAPH_LAYER_CLASS}`)
                .data(this.stackedData)
                .enter()
                .append(G_ELEMENT)
                .classed(AVI_AREA_GRAPH_LAYER_CLASS, true)
                .append(PATH_ELEMENT)
                .classed(AVI_AREA_GRAPH_AREA_CLASS, true)
                .attr('d', this.graphArea as d3.Area<unknown>)
                .attr('fill', data => this.getColor(data.key, false, true))
                .attr('stroke', data => this.getColor(data.key, true, false))
                .attr('stroke-width', 1)
                .attr('opacity', 0.5)
                .transition()
                .duration(animationDuration)
                .ease(d3.easeCubicInOut)
                .attr('opacity', 1);

            this.g
                .selectAll(`.${AVI_AREA_GRAPH_LAYER_Y1_CLASS}`)
                .data(this.stackedY1Data)
                .enter()
                .append(G_ELEMENT)
                .classed(AVI_AREA_GRAPH_LAYER_Y1_CLASS, true)
                .append(PATH_ELEMENT)
                .classed(AVI_AREA_GRAPH_AREA_CLASS, true)
                .attr('d', this.graphY1Area as d3.Area<unknown>)
                .attr('fill', data => this.getColor(data.key, false, true))
                .attr('stroke', data => this.getColor(data.key, true, false))
                .attr('stroke-width', 1)
                .attr('opacity', 0.5)
                .transition()
                .duration(animationDuration)
                .ease(d3.easeCubicInOut)
                .attr('opacity', 1);
        } else {
            this.g
                .selectAll(`.${AVI_AREA_GRAPH_LAYER_CLASS}`)
                .data(this.stackedData)
                .enter()
                .append(G_ELEMENT)
                .classed(AVI_AREA_GRAPH_LAYER_CLASS, true)
                .append(PATH_ELEMENT)
                .classed(AVI_AREA_GRAPH_AREA_CLASS, true)
                .attr('d', this.graphArea as d3.Area<unknown>)
                .attr('fill', data => this.getColor(data.key, false, true))
                .attr('stroke', data => this.getColor(data.key, true, false))
                .attr('stroke-width', 1)
                .attr('opacity', 0.5)
                .transition()
                .duration(animationDuration)
                .ease(d3.easeCubicInOut)
                .attr('opacity', 1);
        }
    }

    private removeToolTip(): void {
        if (!this.showTooltip || this.isGraphHoverPaused) {
            return;
        }

        d3.select(`.${AVI_AREA_GRAPH_TOOLTIP_CLASS}`).remove();
        this.setActiveTimeStamp.emit(0);
        d3.selectAll(`.${AVI_AREA_GRAPH_ACTIVE_TIMESTAMP_CLASS}`).remove();
        d3.selectAll(`.${AVI_AREA_GRAPH_LAYER_CLASS}`).attr('opacity', '1');
        d3.selectAll(`.${AVI_AREA_GRAPH_LAYER_Y1_CLASS}`).attr('opacity', '1');
        this.aviAreaGraphTooltipRef.clear();
    }

    /**
     * Redraw the graph. When the data of the graph changes or when the user zooms in/out.
     * It update the path data of the area graph by re-running the area generator function.
     */
    private redrawGraph(): void {
        this.g
            .selectAll(`.${AVI_AREA_GRAPH_LAYER_CLASS}`)
            .data(this.stackedData)
            .select(PATH_ELEMENT)
            .transition()
            .duration(animationDuration)
            .ease(d3.easeCubicInOut)
            .attr('d', this.graphArea as d3.Area<unknown>);

        if (this.has2YAxis) {
            this.g
                .selectAll(`.${AVI_AREA_GRAPH_LAYER_Y1_CLASS}`)
                .data(this.stackedY1Data)
                .select(PATH_ELEMENT)
                .transition()
                .duration(animationDuration)
                .ease(d3.easeCubicInOut)
                .attr('d', this.graphY1Area as d3.Area<unknown>);
        }
    }

    /**
     * Create scales for the graph.
     * The x-scale is a time scale which maps the timestamps of the data to the x-coordinates of the graph.
     * The y-scale is a linear scale which maps the values of the data to the y-coordinates of the graph.
     */
    private createScales(): void {
        this.xScale = d3
            .scaleTime()
            .domain(d3.extent(this.groupedData, (d: IGroupData) => d.timestamp) as [number, number])
            .range([0, this.graphWidth]);

        if (this.has2YAxis) {
            const leftAxisKeys = this.graphStackKeys.slice(0, -1);
            const rightAxisKeys = this.graphStackKeys.slice(-1);

            const maxYValue = d3
                .max(this.groupedData, (data: IGroupData) => d3
                    .sum(leftAxisKeys, (key: AreaDataPointKeys) => AviAreaGraphComponent
                        .getDataValue(data, key))) as number;
            const maxY = Math.max(this.minYScale, maxYValue);

            this.yScale = d3
                .scaleLinear()
                .domain([0, maxY * yScaleMultiplier])
                .range([this.graphHeight, 0]);

            const maxY1Value = d3
                .max(this.groupedData, (data: IGroupData) => d3
                    .sum(rightAxisKeys, (key: AreaDataPointKeys) => AviAreaGraphComponent
                        .getDataValue(data, key))) as number;
            const maxY1 = Math.max(this.minY1Scale, maxY1Value);

            this.y1Scale = d3
                .scaleLinear()
                .domain([0, maxY1 * yScaleMultiplier])
                .range([this.graphHeight, 0]);
        } else {
            const maxValue = d3
                .max(this.groupedData, (data: IGroupData) => d3
                    .sum(this.graphStackKeys, (key: AreaDataPointKeys) => AviAreaGraphComponent
                        .getDataValue(data, key))) as number;
            const max = Math.max(this.minYScale, maxValue);

            this.yScale = d3
                .scaleLinear()
                .domain([0, max * yScaleMultiplier])
                .range([this.graphHeight, 0]);
        }
    }

    private createXAxis(): void {
        if (!this.showAxis) {
            return;
        }

        const timeFrames = this.timeframeService.selected().timeTickFormat;
        const tickFormatFunction = getTickFormatFunction(timeFrames, this.useUtcTime);

        this.g
            .append(G_ELEMENT)
            .classed(AVI_AREA_GRAPH_X_AXIS_CLASS, true)
            .attr('transform', `translate(0,${this.graphHeight})`)
            .call(
                d3
                    .axisBottom(this.xScale)
                    .ticks(10)
                    .tickSize(1)
                    .tickPadding(5)
                    .tickFormat(tickFormatFunction),
            );
    }

    private createYAxis(): void {
        if (!this.showAxis) {
            return;
        }

        this.g
            .append(G_ELEMENT)
            .classed(AVI_AREA_GRAPH_Y_AXIS_CLASS, true)
            .call(
                d3
                    .axisLeft(this.yScale)
                    .ticks(Math.ceil(this.graphHeight / 50) + 1)
                    .tickSize(-1)
                    .tickFormat(d => num2abbrStr(Number(d), 1)),
            )
            .select('.domain')
            .transition()
            .duration(animationDuration)
            .ease(d3.easeCubicInOut);

        if (this.has2YAxis) {
            this.g
                .append(G_ELEMENT)
                .classed(AVI_AREA_GRAPH_Y1_AXIS_CLASS, true)
                .attr('transform',
                    `translate(${this.graphWidth}, 0)`)
                .call(
                    d3
                        .axisRight(this.y1Scale)
                        .ticks(Math.ceil(this.graphHeight / 50) + 1)
                        .tickSize(-1)
                        .tickFormat(d => num2abbrStr(Number(d), 1)),
                )
                .select('.domain')
                .transition()
                .duration(animationDuration)
                .ease(d3.easeCubicInOut);
        }

        d3.selectAll(`.${AVI_AREA_GRAPH_X_AXIS_CLASS} .tick:first-of-type text`).attr('text-anchor', 'start');
        d3.selectAll(`.${AVI_AREA_GRAPH_X_AXIS_CLASS} .tick:last-of-type text`).attr('text-anchor', 'end');
    }

    /**
     * Update the y-axis with the new maximum value after zooming in/out.
     */
    private updateYAxis(): void {
        if (!this.showAxis) {
            return;
        }

        if (this.has2YAxis) {
            const newMaxValue = d3
                .max(this.groupedData, (data: IGroupData) => {
                    return d3
                        .sum([Object.keys(data).slice(1)[0]], (key: AreaDataPointKeys) => AviAreaGraphComponent
                            .getDataValue(data, key));
                }) as number;
            const max = Math.max(this.minYScale, newMaxValue);
            const newYDomain = [0, max * yScaleMultiplier];

            this.yScale.domain(newYDomain.map(d => d / this.yZoomFactor));
        } else {
            const newMaxValue = d3
                .max(this.groupedData, (data: IGroupData) => d3
                    .sum(Object.keys(data).slice(1), (key: AreaDataPointKeys) => AviAreaGraphComponent
                        .getDataValue(data, key))) as number;
            const max = Math.max(this.minYScale, newMaxValue);
            const newYDomain = [0, max * yScaleMultiplier];

            this.yScale.domain(newYDomain.map(d => d / this.yZoomFactor));
        }

        this.g
            .select(`.${AVI_AREA_GRAPH_Y_AXIS_CLASS}`)
            .call(
                d3
                    .axisLeft(this.yScale)
                    .ticks(Math.ceil(this.graphHeight / 50) + 1)
                    .tickSize(-1)
                    .tickFormat(d => num2abbrStr(Number(d), 1)) as
                (selection: d3.Selection<d3.BaseType, undefined, null, undefined>) => void,
            )
            .select('.domain')
            .transition()
            .duration(animationDuration)
            .ease(d3.easeCubicInOut);

        this.redrawGraph();
    }

    /**
     * This method refreshes the tooltip by creating a new one and positioning it.
     */
    private refreshTooltip(event: MouseEvent, mouseMove = false): void {
        if (!this.showTooltip || this.isGraphHoverPaused) {
            return;
        }

        this.tooltipWidth = tooltipMinWidth;

        const bisectDate = d3.bisector((data: IGroupData) => data.timestamp).left;
        const componentRef = this.createTooltipComponentRef();
        let height = 0;
        const circleWidth = 5;
        const circleStrokeWidth = 2;
        const tooltipLineWidth = 1;
        const timestamp = this.xScale.invert(d3.pointer(event, this.g.node())[0]);
        const index = bisectDate(this.groupedData, timestamp, 1);
        let d0 = this.groupedData[index - 1];
        let d1 = this.groupedData[index];

        if (!d0 || !d1) {
            d0 = this.groupedData[this.groupedData.length - 2];
            d1 = this.groupedData[this.groupedData.length - 1];
        }

        const tooltipData = new Date(timestamp).getTime() - new Date(d0.timestamp).getTime() >
            new Date(d1.timestamp).getTime() - new Date(timestamp).getTime() ? d1 : d0;
        const toolTipLabels: ILabelDataExtended[] = [];

        this.labelList.forEach(label => {
            if (!label.name || label.name && this.hiddenLegendsKeySet.has(label.name)) {
                return;
            }

            const { value, unit } = this.unit && !label.staticUnit ?
                this.convertService.getVal(Number(tooltipData[label.name]) || 0, this.unit) : {
                    unit: label.staticUnit ?? '',
                    value: Number(tooltipData[label.name]) || 0,
                };

            toolTipLabels.push({
                ...label,
                count: `${toFixed(Number(value) || 0, 1)} ${unit}`,
            });
        });

        let tooltipGroup: d3.Selection<d3.BaseType, undefined, HTMLElement, undefined>;

        this.setActiveTimeStamp.emit(tooltipData.timestamp);

        if (mouseMove) {
            tooltipGroup = d3.select(`.${AVI_AREA_GRAPH_TOOLTIP_CLASS}`);

            d3
                .select(`#${AVI_AREA_GRAPH_TOOLTIP_LINE_ID}`)
                .attr('x1', this.xScale(tooltipData.timestamp))
                .attr('x2', this.xScale(tooltipData.timestamp));
        } else {
            tooltipGroup = this.g
                .append(G_ELEMENT)
                .classed(AVI_AREA_GRAPH_TOOLTIP_CLASS, true);

            tooltipGroup
                .append('line')
                .attr('id', AVI_AREA_GRAPH_TOOLTIP_LINE_ID)
                .attr('stroke', TOOLTIP_STROKE)
                .attr('stroke-width', tooltipLineWidth)
                .attr('x1', this.xScale(tooltipData.timestamp))
                .attr('x2', this.xScale(tooltipData.timestamp))
                .attr('y1', 0)
                .attr('y2', this.graphHeight);
        }

        const summarySectionLabel = {
            count: '',
            label: '',
        };

        if (this.hasTotalValueInTooltip) {
            const totalValue = this.labelList.reduce((acc, label) => {
                acc += Number(tooltipData[label.name]) || 0;

                return acc;
            }, 0);

            const { value: formatedTotalValue, unit } = this.unit ?
                this.convertService.getVal(Number(totalValue) || 0, this.unit) : {
                    unit: this.unit ?? '',
                    value: Number(totalValue) || 0,
                };

            summarySectionLabel.count = `${toFixed(Number(formatedTotalValue) || 0, 1)} ${unit}`;
            summarySectionLabel.label = this.l10nService.getMessage(globalL10nKeys.totalLabel);
        }

        attachComponentBindings(componentRef, {
            title: this.getTooltipTitle(new Date(tooltipData.timestamp)),
            labelList: toolTipLabels,
            summarySectionLabel: this.hasTotalValueInTooltip ? summarySectionLabel : undefined,
        });
        this.aviAreaGraphTooltipRef.insert(componentRef.hostView);

        if (this.graphWidth - (this.tooltipWidth + this.xScale(tooltipData.timestamp) + tooltipMarginFromLine) < 0) {
            this.tooltipLeftPositionPx = this.xScale(tooltipData.timestamp) -
                this.tooltipWidth + tooltipMarginFromLine / 2;
        } else {
            this.tooltipLeftPositionPx = this.xScale(tooltipData.timestamp) + tooltipMarginFromLine;
        }

        setTimeout(() => {
            const tooltipContainerWidth = this.aviAreaGraphTooltipContainerElementRef.nativeElement.offsetWidth;

            this.tooltipWidth = tooltipMinWidth > tooltipContainerWidth ? tooltipContainerWidth : tooltipMinWidth;
        }, 0);

        Object.entries(tooltipData).forEach(([key, tooltip], tooltipIndex) => {
            if (this.hiddenLegendsKeySet.has(key) || this.hiddenKeySet.has(key) || key === DATE_KEY) {
                return;
            }

            height += Number(tooltip);

            const xPosition = this.xScale(tooltipData.timestamp);
            let yPosition = 0;

            if (this.has2YAxis && Object.entries(tooltipData).length - 1 === tooltipIndex) {
                yPosition = this.y1Scale(Number(tooltip)) + circleStrokeWidth;
            } else {
                yPosition = this.yScale(height) + circleStrokeWidth;
            }

            if (mouseMove) {
                d3
                    .select(`#${key.replace('.', '-')}`)
                    .attr('transform', `translate(${xPosition}, ${yPosition})`);
            } else {
                tooltipGroup
                    .append('circle')
                    .attr('r', circleWidth)
                    .attr('id', key.replace('.', '-'))
                    .attr('transform', `translate(${xPosition}, ${yPosition})`)
                    .attr('stroke-width', circleStrokeWidth)
                    .attr('stroke', 'var(--cds-global-color-white)')
                    .attr('fill', this.labelList.find(labelObj => labelObj.name === key)?.color);
            }
        });

        d3
            .select(`#${this.graphId}`)
            .selectAll(`.${AVI_AREA_GRAPH_LAYER_CLASS}`)
            .attr('opacity', '0.4');

        d3
            .select(`#${this.graphId}`)
            .selectAll(`.${AVI_AREA_GRAPH_LAYER_Y1_CLASS}`)
            .attr('opacity', '0.4');
    }

    private refreshActiveTimestampCircle(): void {
        const circleWidth = 5;
        const circleStrokeWidth = 1;
        const activeTimestampCircle = this.g.select(`.${AVI_AREA_GRAPH_ACTIVE_TIMESTAMP_CLASS}`);

        if (!this.activeTimestamp) {
            return;
        }

        const xPosition = this.xScale(this.activeTimestamp);
        const yPosition = this.yScale(this.maxValueHash[this.activeTimestamp] ?? 0);

        if (activeTimestampCircle.empty()) {
            this.g
                .append('circle')
                .classed(AVI_AREA_GRAPH_ACTIVE_TIMESTAMP_CLASS, true)
                .attr('r', circleWidth)
                .attr('transform', `translate(${xPosition}, ${yPosition})`)
                .attr('stroke-width', circleStrokeWidth)
                .attr('stroke', 'var(--cds-global-color-white)')
                .attr('fill', TOOLTIP_STROKE);
        } else {
            activeTimestampCircle.attr('transform', `translate(${xPosition}, ${yPosition})`);
        }
    }

    /**
     * Calculate and assign positions for a list of events on the graph.
     * Ensure events do not overlap vertically by adjusting their top positions.
     */
    private calculatePointPositions = (events: IEventsDataPoint[], topPositionHax: { [key: number]: number }): void => {
        if (!events) {
            return;
        }

        const circleWidth = 14;
        const marginBetweenCircles = 2;
        const top = this.yScale(0);

        events.forEach(event => {
            const positionOnX = this.xScale(event.timestamp);
            let left = 0;

            if (positionOnX > this.graphWidth) {
                left = this.graphWidth + circleWidth - marginBetweenCircles;
            } else if (positionOnX + margin.left > this.graphWidth) {
                left = positionOnX;
            } else {
                left = margin.left + positionOnX;
            }

            // Adjust top position to avoid overlap with events at the same left position.
            if (left in topPositionHax) {
                topPositionHax[left] = topPositionHax[left] - circleWidth - marginBetweenCircles;
            } else {
                topPositionHax[left] = top;
            }

            event.left = left;
            event.top = topPositionHax[left];
        });
    };

    /**
     * Calculate positions for the metrics and system events on the graph.
     * We want to position the events in a way that they don't overlap with each other.
     * To do this, we calculate the top position of each event based on the previous event's top position.
     * If there is no previous event, we set the top position to the top of the graph.
     * We also calculate the left position of each event by scaling the timestamp to the x-axis.
     */
    private setEventsPoints = (): void => {
        const topPositionHax: { [key: number]: number } = {};

        this.calculatePointPositions(this.anomalies, topPositionHax);
        this.calculatePointPositions(this.alerts, topPositionHax);
        this.calculatePointPositions(this.configEvents, topPositionHax);
        this.calculatePointPositions(this.systemEvents, topPositionHax);
    };

    /**
     * Return tooltip ref element.
     */
    private createTooltipComponentRef = (): ComponentRef<Component> => {
        this.aviAreaGraphTooltipRef.clear();

        const componentFactory = this.componentFactoryResolver
            .resolveComponentFactory(LabelCountLegendComponent as Type<Component>);

        return componentFactory.create(this.aviAreaGraphTooltipRef.injector);
    };

    /**
     * Return real-time width of graph container element.
     */
    private getGraphContainerWidth = (): number => this.chart.nativeElement.offsetWidth;

    /**
     * Set listener:subscriber to listen to and respond to parent element size change.
     * This is run only once.
     * It must wait till d3 selection of svg is made before running.
     * ngZone needed because anything done in ResizeObserver is not in Angular's zone.
     */
    private setGraphResizeObserver = (): void => {
        this.graphResizeObserver =
            new ResizeObserver(() => {
                this.ngZone.run(this.handleGraphResize);
            });

        this.graphResizeObserver
            .observe(document.querySelector(`#${this.graphId}`));
    };

    /**
     * Handle horizontal resizing of graph as needed.
     * Modifiy size and spacing of x-axis.
     * Throttle used to limit excessive firing, low enough number used to avoid jumpy effect.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private handleGraphResize = throttle(() => {
        const newGraphWidth = this.getGraphContainerWidth();

        if (newGraphWidth === this.graphContainerWidth || !newGraphWidth) {
            return;
        }

        this.graphContainerWidth = newGraphWidth;
        this.svg?.attr('width', this.graphContainerWidth);
        this.graphWidth = +this.svg.attr('width') - this.graphMargin.left - this.graphMargin.right;
        this.xScale.range([0, this.graphWidth]);

        const timeFrames = this.timeframeService.selected().timeTickFormat;
        const tickFormatFunction = getTickFormatFunction(timeFrames, this.useUtcTime);

        this.g
            .select(`.${AVI_AREA_GRAPH_X_AXIS_CLASS}`)
            .call(
                d3
                    .axisBottom(this.xScale)
                    .ticks(10)
                    .tickSize(1)
                    .tickPadding(5)
                    .tickFormat(tickFormatFunction) as
                (selection: d3.Selection<d3.BaseType, undefined, null, undefined>) => void,
            )
            .select('.domain')
            .transition()
            .duration(animationDuration)
            .ease(d3.easeCubicInOut);

        if (this.has2YAxis) {
            this.g
                .select(`.${AVI_AREA_GRAPH_Y1_AXIS_CLASS}`)
                .attr('transform',
                    `translate(${this.graphWidth}, 0)`);
        }

        this.redrawGraph();
        this.setEventsPoints();
        this.removeToolTip();
    }, 30, { leading: false });
}
